import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  message,
  Row,
  Select,
  Spin,
} from "antd";
import locale from 'antd/es/date-picker/locale/es_ES';
import debounce from 'lodash/debounce';
import findIndex from 'lodash/findIndex';
import moment from 'moment';
import 'moment/locale/es';
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getApplicants,
  getBranchOffices,
  getChildCompanies,
  getCostsCenters,
} from '../../../services/general';
import ApplicantModal from './ApplicantModal';
import ChildCompanyModal from './ChildCompanyModal';
import CostCenterModal from './CostCenterModal';
import SelectBox from './form/selectBox';
import codes from '../../../utils/providerCodes.json';

const { Option } = Select;

const GeneralDataCL = ({
  type,
  form,
  reservation,
  setReservation,
  setTotalPatients,
  disablePatientInput,
  setRefreshForm,
  setBranchOfficeId,
  sessionBranchOfficeId,
  fetchCurrency,
  paymentData,
  companies,
  setCompanies,
  countrySettings,
  countryCode,
  paymentAmount,
  isLoadingPaymentAmount,
}) => {
  const [loading, setLoading] = useState(false);
  const [childCompanyLoading, setChildCompanyLoading] = useState(false);
  const [applicantLoading, setApplicantLoading] = useState(false);
  const [applicantList, setApplicantList] = useState([]);
  const [branchOffices, setBranchOffices] = useState();
  const [isDebtor, setIsDebtor] = useState(false);
  const [applicantModal, setApplicantModal] = useState({ type: 'create', visible: false });
  const [childCompanyModal, setChildCompanyModal] = useState({ type: 'create', visible: false });
  const [costCenterModal, setCostCenterModal] = useState({ type: 'create', visible: false });
  const [costsCenters, setCostsCenters] = useState([]);
  const [childCompanies, setChildCompanies] = useState([]);
  const [showChildCompanies, setShowChildCompanies] = useState(false);
  const { t } = useTranslation();

  const addApplicant = (applicant) => {
    const index = findIndex(applicantList, ['id', applicant.id]);
    if (index >= 0) {
      let _applicantList = [...applicantList];
      _applicantList[index] = applicant;
      setApplicantList(_applicantList);
      return null;
    }
    setApplicantList(oldArray => [...(oldArray || []), applicant]);
  };

  const addCompany = (company) => {
    const index = findIndex(companies, ['id', company.id]);
    if (index >= 0) {
      let _companies = [...companies];
      _companies[index] = company;
      setCompanies(_companies);
      return null;
    }
    setCompanies(oldArray => [...(oldArray || []), company]);
  };

  const addChildCompany = (childCompany) => {
    setChildCompanies(oldArray => [...(oldArray || []), childCompany]);
  };

  const addCostCenter = (costCenter) => {
    const index = findIndex(costsCenters, ['id', costCenter.id]);
    if (index >= 0) {
      let _costsCenters = [...costsCenters];
      _costsCenters[index] = costCenter;
      setCostsCenters(_costsCenters);
      return null;
    }
    setCostsCenters(oldArray => [...(oldArray || []), costCenter]);
  };

  const fetchApplicants = debounce(async (query) => {
    setApplicantLoading(true);
    try {
      const response = query ? await getApplicants({ query, limit: 50 }) : [];
      setApplicantList(response.data);
    } catch (error) {
      console.error(`Error interno ${error}`);
      message.error({
        content: `Error interno ${error}`,
        style: { marginTop: '45vh', fontSize: 16 }
      });
    } finally {
      setApplicantLoading(false);
    }
  }, 500);

  const fetchBranchOffices = async () => {
    setLoading(true);
    try {
      const response = await getBranchOffices();
      setBranchOffices(response);
    } catch (error) {
      console.error(`Error al cargar Sucursales: ${error}`);
      message.error({
        content: `Error al cargar Sucursales: ${error}`,
        style: { marginTop: '45vh', fontSize: 16 }
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCostsCenters = async (companyId) => {
    const response = await getCostsCenters({ companyId, limit: 50 });
    setCostsCenters(response);
  };

  const searchChildCompanies = debounce(async (name) => {
    fetchChildCompanies(name);
  }, 500);

  const fetchChildCompanies = async (name) => {
    setChildCompanyLoading(true);
    try {
      const companyId = form.getFieldValue('company_id');
      const response = await getChildCompanies({ companyId, limit: 50, name });
      setChildCompanies(response);
    } catch (error) {
      console.log(error);
      message.error({
        content: `Error interno ${error}`,
        style: { marginTop: '32vh', fontSize: 16 }
      });
    }
    setChildCompanyLoading(false);
  };

  const handleOnChangeSearch = (field, value, option, setter) => {
    if (value) {
      form.setFieldsValue({ [field]: value });
    } else {
      form.setFieldsValue({ [field]: null });
      setter([]);
    }

    if (field === 'company') {
      if (option) {
        setIsDebtor(option.company.is_debtor);
        if (option.company.is_provider) {
          setShowChildCompanies(option.company.is_provider);
          fetchChildCompanies('');
        }
        if (option.company.has_cost_centers) {
          fetchCostsCenters(value);
        } else {
          setCostsCenters([]);
        }
      } else {
        setShowChildCompanies(false);
        setCostsCenters([]);
      }
    }
  };

  const renderCostCenterInput = () => {
    if (costsCenters && costsCenters.length) {
      return (
        <Col lg={8} xs={24}>
          <Row>
            <Col lg={22} xs={22}>
              <Form.Item name="cost_center_id" label="Centro de costo">
                <Select
                  className="cmt-select"
                  placeholder="Ingrese el centro de costo"
                  showArrow={false}
                  showSearch
                  filterOption={true}
                  allowClear
                  onChange={(value, option) => handleOnChangeSearch('costCenter', value, option, setCostsCenters)}
                  onClear={(value, option) => handleOnChangeSearch('costCenter', value, option, setCostsCenters)}
                  notFoundContent={
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        <div className="empty-search">
                          <div>No hay Coincidencias</div>
                          <div>
                            <a onClick={() => setCostCenterModal({ type: 'create', visible: true })}>+ Crear centro de costo</a>
                          </div>
                        </div>
                      }
                    />
                  }
                >
                  {costsCenters?.map(option => (
                    <Option key={option.id} value={option.id}>{option.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col lg={2} xs={2}>
              <Form.Item
                style={{ width: '100%' }}
                label="&nbsp;"
                shouldUpdate={(prevValues, currentValues) => prevValues.cost_center_id !== currentValues.cost_center_id}
              >
                {({ getFieldValue }) =>
                  getFieldValue('cost_center_id') ? (
                    <Button block onClick={() => setCostCenterModal({ type: 'edit', visible: true })} icon={<EditOutlined />} />
                  ) : (
                    <Button block onClick={() => setCostCenterModal({ type: 'create', visible: true })} icon={<PlusOutlined />} />
                  )
                }
              </Form.Item>
            </Col>
          </Row>
        </Col>
      );
    }
  };

  const handlePatientsQuantity = (value) => {
    if (value) {
      setTotalPatients(parseInt(value));
    }
  };

  const disabledDate = (current) => {
    return current && current < moment().endOf('day').subtract(1, 'day');
  }

  useEffect(() => {
    if (reservation && !type) {
      setApplicantList([{ "id": reservation.user_id, "fullname": reservation.user_fullname }]);
      if (reservation.cost_center.id) {
        setCostsCenters([{ "id": reservation.cost_center.id, "code": reservation.cost_center.code, "name": reservation.cost_center.name }]);
      }
      if (reservation.child_company_id) {
        setChildCompanies([{ "id": reservation.child_company.id, "business_name": reservation.child_company.business_name }]);
        setShowChildCompanies(true);
      }
      setCompanies([
        { business_name: reservation.company.business_name, id: reservation.company_id, isComplete: reservation.company.isComplete },
      ]);

      form.setFieldsValue({
        'id': reservation.id,
        'state': reservation.state,
        'user_id': reservation.user_id,
        'user_name': reservation.user_name,
        'company_id': reservation.company_id,
        'child_company_id': reservation.child_company_id,
        'cost_center_id': reservation.cost_center.id,
        'contact_method': reservation.contact_method,
        'branch_office_id': reservation.branch_office_id,
        'reservation_date': moment(reservation.reservation_date, 'YYYY-MM-DD'),
        'total_patients': parseInt(reservation.total_patients),
      });
      setReservation(reservation);
    }
  }, [form, reservation, type]);

  useEffect(() => {
    fetchBranchOffices();
    fetchCurrency();
  }, []);

  return (
    <Row justify="center" className="reservation-settings-wrapper__general-data pt-40 pb-20">
      <Col lg={22} xs={22}>
        <Row>
          <h2>{t('reservations.generalDataForm.title')}</h2>
        </Row>
        <Row gutter={[0, 15]}>
          <Col lg={24} xs={24}>
            <ApplicantModal reservationForm={form} addApplicant={addApplicant} applicantModal={applicantModal} setApplicantModal={setApplicantModal} />
            <ChildCompanyModal reservationForm={form} companyId={form.getFieldValue('company_id')} addChildCompany={addChildCompany} childCompanyModal={childCompanyModal} setChildCompanyModal={setChildCompanyModal} />
            <CostCenterModal reservationForm={form} companyId={form.getFieldValue('company_id')} addCostCenter={addCostCenter} costCenterModal={costCenterModal} setCostCenterModal={setCostCenterModal} />
            <Form form={form} size="large" layout="vertical">
              <Row gutter={15}>
                <Col lg={8} xs={24}>
                  <Form.Item name="state" style={{ display: 'none' }}><Input type="hidden" /></Form.Item>
                  <Form.Item name="id" style={{ display: 'none' }}><Input type="hidden" /></Form.Item>
                  <Form.Item name="branch_office" style={{ display: 'none' }}><Input type="hidden" /></Form.Item>
                  <Spin className="cmt-spin" spinning={applicantLoading}>
                    <Row>
                      <Col lg={22} xs={22}>
                        <Form.Item name="user_id" label={t('reservations.generalDataForm.applicantLabel')} rules={[{ required: true, message: 'Solicitante es requerido' }]}>
                          <Select className="cmt-select" placeholder={t('reservations.generalDataForm.applicantPlaceholder')} showSearch showArrow={false} filterOption={false} allowClear onSearch={fetchApplicants} onChange={(value, option) => handleOnChangeSearch('applicant', value, option, setApplicantList)} onClear={() => handleOnChangeSearch('applicant', null, null, setApplicantList)}
                            notFoundContent={
                              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={
                                <div className="empty-search">
                                  <div>No hay coincidencias</div>
                                  <div><Button type="link" onClick={() => setApplicantModal({ type: 'create', visible: true })}>{t('reservations.generalDataForm.createApplicantButton')}</Button></div>
                                </div>
                              } />
                            }
                          >
                            {applicantList?.map(option => (
                              <Option key={option.id} value={option.id}>{option.fullname}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col lg={2} xs={2}>
                        <Form.Item style={{ width: '100%' }} label="&nbsp;" shouldUpdate={(prevValues, currentValues) => prevValues.user_id !== currentValues.user_id}>
                          {({ getFieldValue }) => getFieldValue('user_id') ? (
                            <Button block onClick={() => setApplicantModal({ type: 'edit', visible: true })} icon={<EditOutlined />} id="editApplicant" />
                          ) : (
                            <Button block onClick={() => setApplicantModal({ type: 'create', visible: true })} icon={<PlusOutlined />} id="createNewApplicant" />
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                  </Spin>
                </Col>

                <SelectBox sessionBranchOfficeId={sessionBranchOfficeId} handleOnChangeSearch={handleOnChangeSearch} setter={setCompanies} list={companies} formName="company_id" label={t('reservations.generalDataForm.companyLabel')} placeholder={t('reservations.generalDataForm.companyPlaceholder')} buttonText={t('reservations.generalDataForm.createCompanyButton')} field='company' requireMessage='Compañía es requerida' editButtonId='editCompany' createButtonId='createNewCompany' shouldUpdate={(prevValues, currentValues) => prevValues.company_id !== currentValues.company_id} handleModalResult={addCompany} form={form} createTitle={t('reservations.companyModal.createCompanyTitle')} editTitle={t('reservations.companyModal.updateCompanyTitle')} setModal='setCompanyModal' fetch='fetchCompanies' />

                {showChildCompanies && (
                  <Col lg={8} xs={24}>
                    <Spin className="cmt-spin" spinning={childCompanyLoading}>
                      <Row>
                        <Col lg={22} xs={22}>
                          <Form.Item name="child_company_id" label={t('reservations.generalDataForm.subCompanyLabel')}>
                            <Select className="cmt-select" placeholder={t('reservations.generalDataForm.subCompanyPlaceholder')} showSearch showArrow={false} filterOption={false} allowClear onSearch={searchChildCompanies} onChange={(value, option) => handleOnChangeSearch('subCompany', value, option, setChildCompanies)} onClear={() => handleOnChangeSearch('subCompany', null, null, setChildCompanies)}
                              notFoundContent={
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={
                                  <div className="empty-search">
                                    <div>No hay coincidencias</div>
                                    <div><Button type="link" onClick={() => setChildCompanyModal({ type: 'create', visible: true })}>{t('reservations.generalDataForm.createSubCompanyButton')}</Button></div>
                                  </div>
                                } />
                              }
                            >
                              {childCompanies?.map(option => (
                                <Option key={option.id} value={option.id}>{option.business_name}</Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col lg={2} xs={2}>
                          <Form.Item style={{ width: '100%' }} label="&nbsp;" shouldUpdate={(prevValues, currentValues) => prevValues.child_company_id !== currentValues.child_company_id}>
                            {({ getFieldValue }) => getFieldValue('child_company_id') ? (
                              <Button block onClick={() => setChildCompanyModal({ type: 'edit', visible: true })} icon={<EditOutlined />} />
                            ) : (
                              <Button block onClick={() => setChildCompanyModal({ type: 'create', visible: true })} icon={<PlusOutlined />} />
                            )}
                          </Form.Item>
                        </Col>
                      </Row>
                    </Spin>
                  </Col>
                )}
                {renderCostCenterInput()}
                <Col lg={8} xs={24}>
                  <Form.Item name="contact_method" label={t('reservations.generalDataForm.contactMethodLabel')} className="general-data contact-method select-wrapper">
                    <Select className="cmt-select" placeholder={t('reservations.generalDataForm.contactMethodPlaceholder')}>
                      <Option value={"way_web"}>Web</Option>
                      <Option value={"way_phone"}>Teléfono</Option>
                      <Option value={"way_email"}>Correo electrónico</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col lg={8} xs={24} className="general-data branch-office select-wrapper">
                  <Form.Item name="branch_office_id" label={t('reservations.generalDataForm.branchOfficeLabel')}>
                    {loading ? <Row justify="center"><Spin className="cmt-spin" /></Row> : (
                      <Select showSearch className="cmt-select" placeholder={t('reservations.generalDataForm.branchOfficePlaceholder')} onChange={(value) => { setRefreshForm(new Date().getUTCMilliseconds()); setBranchOfficeId(value) }} filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
                        {branchOffices?.map((office, index) => (
                          <Option key={index} value={office.id}>{office.name}</Option>
                        ))}
                      </Select>
                    )}
                  </Form.Item>
                </Col>
                <Col lg={8} xs={24}>
                  <Form.Item name="reservation_date" label={t('reservations.generalDataForm.dateLabel')} help="Debe ingresar una fecha mayor a la de hoy" rules={[{ required: true, message: `Fecha de ${t('reservations.generalDataForm.reservation').toLocaleLowerCase()} es requerida` }, { validator: async (_, endDatetime) => { const current = endDatetime; if (current && current < moment().endOf('day').subtract(1, 'day')) { return Promise.reject("Debe ingresar una fecha mayor a la de hoy"); } } }]}>
                    <DatePicker onChange={() => setRefreshForm(new Date().getUTCMilliseconds())} className="cmt-date-picker" locale={locale} placeholder={t('reservations.generalDataForm.datePlaceholder')} popupClassName="custom-dropdown" style={{ width: "100%" }} disabledDate={disabledDate} format="DD-MM-YYYY" />
                  </Form.Item>
                </Col>
                <Col lg={8} xs={24}>
                  <Form.Item name="total_patients" label={t('reservations.generalDataForm.totalPatientsLabel')} rules={[{ required: true, message: 'Total de trabajadores es requerido', type: 'integer' }]}>
                    <InputNumber min={1} max={100} className="cmt-input-number" style={{ width: '100%' }} placeholder={t('reservations.generalDataForm.totalPatientsPlaceholder')} onChange={handlePatientsQuantity} disabled={disablePatientInput} />
                  </Form.Item>
                </Col>
              </Row>
              {isDebtor && <Row>
                <Col className="mt-20 mb-20">
                  <Alert
                    className="cmt-alert"
                    message={
                      <div dangerouslySetInnerHTML={{ __html: t('reservations.generalDataForm.blockedClient') }} />
                    }
                    type="info"
                    showIcon />
                </Col>
              </Row>}
              <Row gutter={15} className="mt-20">
                <Col lg={8} xs={24}>
                  <p className="mb-0">
                    Método de pago:
                    <strong className="ml-5">
                      {
                        paymentData && paymentData.length > 0 && reservation?.payment_methods?.length > 0 ?
                          (
                            paymentData.find(payment => payment.id === reservation.payment_methods[0])
                            || { name: 'No encontrado' }
                          ).name
                          :
                          'No seleccionado'
                      }
                    </strong>
                  </p>
                </Col>
                {
                  reservation?.payment_methods_codes && reservation.payment_methods_codes.length > 0 &&
                  (reservation.payment_methods_codes.includes(codes.mercadopagocl) || reservation.payment_methods_codes.includes(codes.toku)) &&
                  <>
                    <Col lg={8} xs={24}>
                      <p className="mb-0">
                        Estado de pago:
                        <strong className="ml-5">{reservation.paid ? 'Pagado' : 'No pagado'}</strong>
                      </p>
                    </Col>
                    <Col lg={8} xs={24}>
                      <p className="mb-0">
                        Monto pagado:
                        {
                          isLoadingPaymentAmount ? (
                            <span className="payment-amount-spinner">
                              <Spin size="small" className="cmt-spin" />
                            </span>
                          ) : (
                            <strong className="ml-5">
                              {paymentAmount?.total
                                ? `$${paymentAmount.total.toLocaleString('es-CL')}`
                                : '$0'}
                            </strong>
                          )
                        }
                      </p>
                    </Col>
                  </>
                }
              </Row>
            </Form>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default GeneralDataCL;
